import GenericApi from "@/api/genericApi";
import {
  ContentTypeQueryKey,
  GroupQueryKey,
  UsersQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import { ListContentType, PermissionButtonState } from "@/types/ContentTypes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { Fragment, useEffect, useState } from "react";
import PermissionButton from "./PermissionButton";
import { generatePermissionsMap, isBitramp } from "@/utils/utils";
import { ListGroup, UpdateGroup } from "@/types/Groups";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { Staff } from "@/types/User";
import { AddMembers, Members } from "@/components/index.tsx";

interface GroupCardProps {
  name: string;
  onDelete: () => void;
  onEdit: (id: number) => void;
  isEditing: boolean;
  group: ListGroup;
  users: Staff[];
}

const GroupCard: React.FC<GroupCardProps> = ({
  name,
  onDelete,
  onEdit,
  isEditing,
  users,
  group,
}) => {
  // Starea pentru a controla extinderea cardului
  const [isExpanded, setIsExpanded] = useState(false);
  const queryClient = useQueryClient();
  // Funcție pentru a alterna extinderea cardului
  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const { data: contentType, isSuccess } = useQuery<
    ApiResponse<ListContentType[]>
  >({
    queryFn: async () =>
      GenericApi.get<ListContentType[]>(ApiRoutes.CONTENTYPES),
    queryKey: [ContentTypeQueryKey],
  });

  const [showAddMembers, setShowAddMembers] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      setPermissionMap(generatePermissionsMap(contentType?.data, group));
    }
  }, [isSuccess]);

  const defaultPermissionsMap = generatePermissionsMap(
    contentType?.data ?? null,
    group
  );

  const [permissionMap, setPermissionMap] = useState<PermissionButtonState>({
    ...defaultPermissionsMap,
  });

  const handleButtonClick = (permissionName: string) => {
    setPermissionMap((prev) => {
      return { ...prev, [permissionName]: !prev[permissionName] };
    });
  };

  const editGroupMutation = useMutation<
    ApiResponse<UpdateGroup>,
    ApiError<ValidationErrors>,
    UpdateGroup
  >({
    mutationFn: (data) =>
      GenericApi.put<UpdateGroup>(`${ApiRoutes.GROUP}/${data.id}`, {
        ...data,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      await queryClient.invalidateQueries({ queryKey: [GroupQueryKey] });
      await queryClient.invalidateQueries({ queryKey: [UsersQueryKey] });
      setIsExpanded(false);
      setShowAddMembers(false);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const checkActiveButtons = (permissionName: string) =>
    !!permissionMap[permissionName];

  const [groupName, setGroupName] = useState<string>(name);

  const handleSave = () => {
    if (!group) {
      //TODO ALERT THE USER
      return;
    }

    editGroupMutation.mutate({
      id: group.id,
      name: groupName,
      permissions: Object.entries(permissionMap)
        .map(([key]) => {
          if (permissionMap[key]) {
            return key;
          }
        })
        .filter((perm) => perm !== undefined),
      users: group?.users.map((user) => user.id),
    });
  };

  const handleDeleteUserFromGroup = (userId: number) => {
    const editedGroupUsers = group?.users.filter(
      (userValue) => userValue.id !== userId
    );

    editGroupMutation.mutate({
      id: group.id,
      name: groupName,
      permissions: Object.entries(permissionMap)
        .map(([key]) => {
          if (permissionMap[key]) {
            return key;
          }
        })
        .filter((perm) => perm !== undefined),
      users: editedGroupUsers.map((user) => user.id),
    });
  };

  const handleAddUserToGroup = (userIds: number[]) => {
    onEdit(-1);

    editGroupMutation.mutate({
      id: group.id,
      name: groupName,
      permissions: Object.entries(permissionMap)
        .map(([key]) => {
          if (permissionMap[key]) {
            return key;
          }
        })
        .filter((perm) => perm !== undefined),
      users: [...userIds],
    });
  };

  const handleAddMembers = () => {
    setShowAddMembers(true);
  };

  return (
    <div
      className={`groupCard Container py-3 ${
        isBitramp() ? "!bg-[#232323]" : "!bg-[#f5f5f5]"
      }`}
    >
      <div className="w-full flex items-center justify-between">
        <div className="flex justify-start items-center gap-2">
          {isEditing ? (
            <Fragment>
              <input
                className="w-full p-2 text-gray-700 border border-gray-300 Input Text dark-blue"
                type="text"
                defaultValue={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </Fragment>
          ) : (
            <div className="groupCard__name Tex dark-blue">{name}</div>
          )}
        </div>

        <div className="groupCard__delete">
          <div
            className={` !outline-none ${
              isBitramp() ? "Edit_button_bitramp" : "Outline_button"
            }`}
            onClick={() => {
              onEdit(group.id);
              setIsExpanded(true);
            }}
          >
            edit
          </div>
          <span
            className={` ${
              isBitramp() ? "Delete_button_bitramp" : "Outline_red_button"
            }`}
            onClick={onDelete}
          >
            delete
          </span>

          <span onClick={toggleExpand}>
            {isExpanded ? (
              <svg
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.61867 0.643939C4.99574 -0.0594038 6.00427 -0.0594026 6.38133 0.64394L10.3397 8.02751C10.6969 8.69368 10.2143 9.5 9.45841 9.5L1.54159 9.5C0.785718 9.5 0.303111 8.69368 0.660255 8.02751L4.61867 0.643939Z"
                  fill={`${isBitramp() ? "#ffffff" : "#222B45"}`}
                />
              </svg>
            ) : (
              <svg
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660253 1.97249C0.30311 1.30632 0.785717 0.500001 1.54159 0.500001L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                  fill={`${isBitramp() ? "#ffffff" : "#222B45"}`}
                />
              </svg>
            )}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="w-full mb-3">
          <div className="flex justify-center items-center gap-4">
            <Members
              handleAddMembers={handleAddMembers}
              members={group?.users}
              handleDeleteUserFromGroup={handleDeleteUserFromGroup}
            />
            <div className="flex-1 px-5 min-h-[25rem]">
              {!showAddMembers ? (
                <div className="flex justify-end items-center gap-4 py-3">
                  <div className="flex flex-col items-center justify-end h-[3rem] gap-2 ml-2">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.837 0L29.4959 0.690241C30.27 1.16117 31.2033 2.21604 31.5661 3.04621C31.7401 3.44313 31.7698 3.93155 32.0004 4.28811V6.16373L31.175 8.08645L30.3253 9.11441L16.2829 23.1076C15.9835 23.2825 15.6315 23.4171 15.2836 23.4507C14.0091 23.5758 11.1688 23.5839 9.90235 23.4507C9.18216 23.3754 8.56446 22.6044 8.49164 21.9101C8.36216 20.6749 8.36216 17.7754 8.49164 16.5402C8.5267 16.2079 8.66561 15.9603 8.83825 15.6804C13.8675 10.7438 18.7186 5.60266 23.8948 0.823445L25.957 0H27.837ZM26.456 3.37855C25.609 3.52655 24.9563 4.48051 24.3453 5.02409L26.9631 7.63571C27.9206 6.71538 29.303 5.56229 28.2996 4.15894C27.895 3.59248 27.1519 3.25611 26.4546 3.37855H26.456ZM11.8539 20.0977H14.473L24.4181 10.1854V9.91364L22.0633 7.56439L21.8732 7.50923L11.8552 17.4861V20.0991L11.8539 20.0977Z"
                        fill={`${isBitramp() ? "#9F9F9F" : "#222B45"}`}
                      />
                      <path
                        d="M4.43231 6.73175C5.09451 6.64294 6.42835 6.62545 7.09189 6.69542C8.16274 6.80709 8.91125 8.31944 8.28952 9.23976C7.13235 10.9553 4.47007 9.05408 3.55297 11.2136V27.3757C3.80922 27.879 4.35408 28.3579 4.93267 28.4104L20.7607 28.3808C22.6178 27.7525 21.5079 25.3736 22.0797 24.3376C22.8161 23.0015 24.9322 23.1939 25.2585 24.7466C25.4393 25.6024 25.3111 27.6368 25.0212 28.4777C24.2578 30.687 22.2254 31.7473 19.9839 31.8939C15.1597 31.6289 9.9848 32.2343 5.2024 31.8939C2.70466 31.7163 0.839439 30.3479 0.19747 27.9099C-0.0817056 22.1888 -0.042594 16.3857 0.175891 10.6593C0.606118 8.63294 2.37288 7.00757 4.43231 6.73175Z"
                        fill={`${isBitramp() ? "#9F9F9F" : "#222B45"}`}
                      />
                    </svg>
                    <p className={`${isBitramp() ? "text-white" : ""}`}>Edit</p>
                  </div>

                  <div className="flex flex-col items-center justify-end h-[3rem] gap-2">
                    <svg
                      width="35"
                      height="12"
                      viewBox="0 0 45 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_3544_10279)">
                        <path
                          d="M2.01324 12.931C1.97926 12.8937 1.88218 12.9009 1.8482 12.8648L0 10.5507C1.01087 9.39482 1.99746 8.20885 3.14182 7.17579C9.80772 1.15686 18.4383 -1.28371 27.3226 0.649961C34.1802 2.14175 40.5815 6.31491 45.0012 11.6343C41.448 15.3716 36.8354 18.4732 31.94 20.2154C20.9079 24.1418 9.81136 21.535 2.01445 12.931H2.01324ZM15.2795 4.83155C15.112 4.6967 12.8573 5.57564 12.4884 5.73457C10.4727 6.60147 8.12213 8.08002 6.48023 9.52606C6.29335 9.69101 5.29947 10.6326 5.33224 10.7807C5.79823 11.2406 6.23995 11.7342 6.72294 12.1761C8.95219 14.2182 12.3185 16.2241 15.2771 17.0536C15.5526 17.1307 15.8414 17.2186 16.129 17.2318C16.1727 17.1969 15.9433 16.9706 15.8899 16.926C12.8779 14.3747 12.259 9.45622 14.3305 6.11625C14.6145 5.65751 15.0113 5.29751 15.2807 4.83155H15.2795ZM27.6575 17.3522C31.3308 16.4673 34.9241 14.8154 37.964 12.5879C38.1921 12.4205 39.3522 11.6223 39.3025 11.4031C37.0089 9.25635 34.2712 7.41419 31.3818 6.1319C31.0808 5.99825 29.2217 5.21925 29.1125 5.31316C30.8515 7.94878 31.3587 11.0118 30.144 13.9822C29.5991 15.3138 28.6635 16.3782 27.6563 17.3522H27.6575ZM19.5584 13.108C22.4248 15.5847 26.6952 12.5674 25.0933 9.1215C24.9586 8.83013 24.4647 8.09687 24.1989 7.96202C23.9477 7.8356 24.0424 8.16912 24.0205 8.26544C23.7038 9.65127 23.324 10.6855 22.0704 11.5091C21.1821 12.0918 20.1118 12.3832 19.0414 12.2965L19.5584 13.108Z"
                          fill={`${isBitramp() ? "#9F9F9F" : "#222B45"}`}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_3544_10279">
                          <rect width="45" height="22" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <p className={`${isBitramp() ? "text-white" : ""}`}>View</p>
                  </div>
                </div>
              ) : null}

              {!showAddMembers ? (
                <div className="flex flex-col items-end justify-center gap-4 py-3">
                  {contentType?.data?.map((cont, index) => {
                    return (
                      <div
                        key={cont.id}
                        className={`groupCard__details__item flex justify-between max-w-[20rem] w-full items-center border-b-2 ${
                          index === 0 ? "border-t-2 pt-2" : ""
                        } ${isBitramp() ? "border-[#464646]" : ""}`}
                      >
                        <div className="text-left Text dark-blue">
                          {cont?.display_name}
                        </div>
                        <div className="flex justify-center items-center gap-9 py-2">
                          {cont.permissions.map((perm) => {
                            const isActive = checkActiveButtons(perm);
                            const icon = isActive ? (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 26 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M26.0001 2.61196V3.22823C25.9078 3.36298 25.8922 3.54286 25.8255 3.70233C25.7701 3.83523 25.6921 3.98296 25.5974 4.09299C20.9405 8.76229 16.1851 13.3376 11.5388 18.0168C11.0538 18.5052 10.6124 19.08 10.0807 19.53C9.51459 20.0084 8.90801 20.1945 8.24346 19.7445L0.406551 11.9796C-0.618961 10.561 0.489465 9.30871 1.58979 8.40378C2.35284 7.77576 3.31539 7.72693 4.14079 8.28015L8.95913 13.0595L9.04766 13.0848L21.7696 0.484381C23.403 -0.808115 24.8829 0.753883 25.7981 2.04205L26.0001 2.61196Z"
                                  fill={`${isBitramp() ? "#ffffff" : "black"}`}
                                />
                              </svg>
                            ) : (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clip-path="url(#clip0_3544_10230)">
                                  <path
                                    d="M2.84646 0L3.50487 0.159289L4.30476 0.568178L8.00133 4.26667L11.6275 0.640711C11.8849 0.431644 12.3869 0.151467 12.709 0.0867556C12.8562 0.0568889 13.0666 0.127289 13.1562 0.000711111H13.5117C13.602 0.130133 13.8125 0.0547556 13.9589 0.0867556C14.8612 0.283733 15.6796 1.09013 15.8972 1.98756C15.9327 2.13333 15.8865 2.33102 15.9996 2.41849V2.91627L15.7507 3.7696L15.4307 4.30293L11.7342 8.00071L15.3596 11.6274C15.5943 11.9595 15.8296 12.3022 15.9135 12.709C15.9441 12.8562 15.8716 13.0667 15.9996 13.1563V13.5829C15.8851 13.6704 15.9341 13.8674 15.8972 14.0139C15.7038 14.779 15.1442 15.4176 14.4353 15.7518L13.5821 16.0007H13.0844L12.2312 15.7518L11.6979 15.4318L8.00062 11.7348L4.30334 15.4318L3.77008 15.7518L2.91686 16.0007H2.41914C2.33169 15.8862 2.13474 15.9353 1.98827 15.8983C1.10661 15.675 0.273297 14.858 0.0877216 13.9598C0.0571479 13.8126 0.129672 13.6021 0.00168866 13.5125V13.157C0.131094 13.0667 0.0557259 12.8562 0.0877216 12.7097C0.176599 12.3029 0.404124 11.9602 0.641603 11.6281L4.26708 8.00142L0.643025 4.37333L0.180865 3.55556L0.00239968 2.77333C0.00666578 2.70364 -0.00471048 2.62969 0.00239968 2.56C0.0194641 2.39573 0.10763 2.02169 0.162378 1.83111C0.38706 1.05458 1.20117 0.283022 1.98969 0.1024C2.11625 0.0732445 2.40421 0.00924444 2.49167 0H2.84718H2.84646Z"
                                    fill={`${
                                      isBitramp() ? "#ffffff" : "#c5cad6"
                                    }`}
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_3544_10230">
                                    <rect width="16" height="16" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            );
                            const title = perm
                              .split("_")
                              .map((word) => {
                                return (
                                  word.charAt(0).toUpperCase() + word.slice(1)
                                );
                              })
                              .join(" ");

                            return (
                              <div
                                className="groupCard__details__item__button"
                                key={perm}
                              >
                                <PermissionButton
                                  isDisabled={!isEditing}
                                  onClick={() => handleButtonClick(perm)}
                                  svgElement={icon}
                                  isActive={isActive}
                                  title={title}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {isEditing ? (
                    <div className="flex mt-3 justify-end items-center">
                      <button
                        className={`${
                          isBitramp()
                            ? "Cancel_button_bitramp"
                            : "Outline_red_button"
                        }  mr-4`}
                        onClick={() => onEdit(-1)}
                      >
                        Cancel
                      </button>
                      <button
                        className="my-4"
                        onClick={() => {
                          if (isEditing) {
                            handleSave();
                            onEdit(-1);
                            return;
                          }
                          onEdit(group.id);
                          setIsExpanded(true);
                        }}
                      >
                        <div
                          className={`${
                            isBitramp()
                              ? "Upload_button_bitramp"
                              : "Primary_button"
                          } `}
                        >
                          Save
                        </div>
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <Fragment>
                  <AddMembers
                    members={users}
                    groupUsers={group?.users}
                    handleAddUserToGroup={handleAddUserToGroup}
                    onClose={() => setShowAddMembers(false)}
                  />
                </Fragment>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupCard;
